import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Save, Factory, PencilLine, Plus, ArrowLeft } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatMonto } from '../utils/format';
import PendientesProduccion from '../components/PendientesProduccion';
import BotonAyuda from '../components/BotonAyuda';

function pad(n) {
  return String(n).padStart(2, '0');
}

function fechaHoy() {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export default function ProduccionForm() {
  const { empresaActiva, modulosActivos } = useAuth();
  const [searchParams] = useSearchParams();
  const [fecha, setFecha] = useState(searchParams.get('fecha') || fechaHoy());
  const [turno, setTurno] = useState(searchParams.get('turno') === 'nocturno' ? 'nocturno' : 'diurno');
  const [maquinas, setMaquinas] = useState([]);
  const [kgPorMaquina, setKgPorMaquina] = useState({});
  const [existente, setExistente] = useState(false);
  const [noTrabajado, setNoTrabajado] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const [guardadoOk, setGuardadoOk] = useState(false);

  useEffect(() => {
    if (!empresaActiva) return;
    api.get('/produccion/maquinas').then(({ data }) => setMaquinas(data));
  }, [empresaActiva]);

  const cargarRegistro = useCallback(async () => {
    if (!empresaActiva) return;
    setCargando(true);
    setGuardadoOk(false);
    setError('');
    const { data } = await api.get('/produccion', { params: { fecha, turno } });
    if (data) {
      setExistente(true);
      setNoTrabajado(data.estado === 'no_trabajado');
      const mapa = {};
      data.detalle.forEach((d) => {
        mapa[d.maquina_id] = d.kg;
      });
      setKgPorMaquina(mapa);
    } else {
      setExistente(false);
      setNoTrabajado(false);
      setKgPorMaquina({});
    }
    setCargando(false);
  }, [empresaActiva, fecha, turno]);

  useEffect(() => {
    cargarRegistro();
  }, [cargarRegistro]);

  function cambiarKg(maquinaId, valor) {
    setKgPorMaquina((prev) => ({ ...prev, [maquinaId]: valor }));
  }

  async function guardar(e) {
    e.preventDefault();
    setError('');
    setGuardando(true);
    try {
      const detalle = maquinas.map((m) => ({ maquina_id: m.id, kg: Number(kgPorMaquina[m.id]) || 0 }));
      await api.post('/produccion', { fecha_produccion: fecha, turno, detalle });
      setExistente(true);
      setNoTrabajado(false);
      setGuardadoOk(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar la producción');
    } finally {
      setGuardando(false);
    }
  }

  async function marcarNoTrabajado() {
    setError('');
    setGuardando(true);
    try {
      await api.post('/produccion', { fecha_produccion: fecha, turno, no_trabajado: true });
      setExistente(true);
      setNoTrabajado(true);
      setKgPorMaquina({});
      setGuardadoOk(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al marcar el turno como no trabajado');
    } finally {
      setGuardando(false);
    }
  }

  function irAPendiente(p) {
    setFecha(p.fecha);
    setTurno(p.turno);
  }

  const totalKg = maquinas.reduce((acc, m) => acc + (Number(kgPorMaquina[m.id]) || 0), 0);

  if (!modulosActivos?.produccion) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-8 text-center text-slate-500">
        El módulo de Producción no está habilitado para esta empresa.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Link to="/produccion" className="p-2 -ml-2 rounded-md text-slate-500 hover:bg-slate-100 transition-colors">
            <ArrowLeft size={18} />
          </Link>
          <h1 className="text-2xl font-bold text-slate-800">Cargar Producción<BotonAyuda /></h1>
        </div>
      </div>

      <PendientesProduccion onSeleccionar={irAPendiente} />

      <form onSubmit={guardar} className="bg-white rounded-xl shadow-sm p-5 space-y-5">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-sm text-slate-500 mb-1">Fecha de producción</label>
            <input
              type="date"
              value={fecha}
              max={fechaHoy()}
              onChange={(e) => setFecha(e.target.value)}
              className="border rounded-md px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm text-slate-500 mb-1">Turno</label>
            <div className="flex rounded-md border overflow-hidden">
              {['diurno', 'nocturno'].map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTurno(t)}
                  className={`px-4 py-2 text-sm font-medium capitalize transition-colors ${
                    turno === t ? 'bg-teal-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <span
            className={`flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 ${
              noTrabajado
                ? 'bg-slate-100 text-slate-600'
                : existente
                  ? 'bg-amber-100 text-amber-700'
                  : 'bg-teal-100 text-teal-700'
            }`}
          >
            {existente ? <PencilLine size={13} /> : <Plus size={13} />}
            {noTrabajado ? 'Marcado como no trabajado' : existente ? 'Editando registro existente' : 'Nuevo registro'}
          </span>
        </div>

        {noTrabajado && (
          <p className="text-xs text-slate-500">
            Este turno está marcado como no trabajado. Si cargás los kg y guardás, se revierte automáticamente.
          </p>
        )}

        {cargando ? (
          <p className="text-sm text-slate-400">Cargando...</p>
        ) : maquinas.length === 0 ? (
          <p className="text-sm text-slate-400">No hay máquinas activas configuradas para esta empresa.</p>
        ) : (
          <div
            className="grid gap-x-6 gap-y-3 lg:grid-cols-2"
            style={{ gridTemplateColumns: 'minmax(0, 1fr)' }}
          >
            <div className="grid items-center gap-x-3 gap-y-3" style={{ gridTemplateColumns: 'auto minmax(0, 1fr) auto' }}>
              {maquinas.slice(0, Math.ceil(maquinas.length / 2)).map((m) => (
                <React.Fragment key={m.id}>
                  <label className="flex items-center gap-1.5 text-sm text-slate-600 whitespace-nowrap">
                    <Factory size={14} className="text-teal-600 flex-shrink-0" />
                    {m.nombre}
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={kgPorMaquina[m.id] ?? ''}
                    onChange={(e) => cambiarKg(m.id, e.target.value)}
                    placeholder="0"
                    className="w-full border rounded-md px-3 py-2 tabular-nums"
                  />
                  <span className="text-xs text-slate-400">kg</span>
                </React.Fragment>
              ))}
            </div>
            <div className="grid items-center gap-x-3 gap-y-3" style={{ gridTemplateColumns: 'auto minmax(0, 1fr) auto' }}>
              {maquinas.slice(Math.ceil(maquinas.length / 2)).map((m) => (
                <React.Fragment key={m.id}>
                  <label className="flex items-center gap-1.5 text-sm text-slate-600 whitespace-nowrap">
                    <Factory size={14} className="text-teal-600 flex-shrink-0" />
                    {m.nombre}
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={kgPorMaquina[m.id] ?? ''}
                    onChange={(e) => cambiarKg(m.id, e.target.value)}
                    placeholder="0"
                    className="w-full border rounded-md px-3 py-2 tabular-nums"
                  />
                  <span className="text-xs text-slate-400">kg</span>
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-4">
          <p className="text-sm text-slate-500">
            Total: <span className="font-semibold text-slate-800 tabular-nums">{formatMonto(totalKg)} kg</span>
          </p>
          <div className="flex items-center gap-3">
            {guardadoOk && <span className="text-sm text-teal-600 font-medium">Guardado ✓</span>}
            {!noTrabajado && (
              <button
                type="button"
                onClick={marcarNoTrabajado}
                disabled={guardando || cargando}
                className="flex items-center gap-2 bg-slate-100 text-slate-600 rounded-md px-4 py-2 hover:bg-slate-200 disabled:opacity-50"
              >
                No trabajado
              </button>
            )}
            <button
              type="submit"
              disabled={guardando || cargando || maquinas.length === 0}
              className="flex items-center gap-2 bg-primary-600 text-white rounded-md px-4 py-2 hover:bg-primary-700 disabled:opacity-50"
            >
              <Save size={16} />
              {guardando ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>

        {error && <p className="text-red-600 text-sm">{error}</p>}
      </form>
    </div>
  );
}
